import type { BookedAppointment, ScreeningProgram } from "./hooks"
import { assign, setup } from "xstate"

// Stashed on DISQUALIFIED so NotEligiblePage can show the right copy.
export type ScreeningExclusion = { exclusionMessageId: string; emergency: boolean }

// The member's selections on the "Select your preferences" screen — the visit type and
// location that key the availability lookup. `region` is a US state code.
export type BookingPreferences = { appointmentTypeId: string; region: string }

// The slot the member picked from availability. Mirrors the fields of the scheduling-api
// `Slot` the later booking call needs (the opaque `slotToken` to redeem, and a `hostId`
// chosen from `availableHostIds`); `startTime` is UTC ISO. Carried now so the booking
// ticket doesn't have to reshape the flow.
export type SelectedSlot = {
  slotToken: string
  startTime: string
  offeringId: string
  availableHostIds: string[]
}

export type BookingFlowInput = { alreadySignedConsent: boolean; program: ScreeningProgram }
export type BookingFlowContext = {
  alreadySignedConsent: boolean
  program: ScreeningProgram
  exclusion: ScreeningExclusion | null
  preferences: BookingPreferences | null
  selectedSlot: SelectedSlot | null
  // The appointment returned by the booking call, shown on the confirmation screen.
  appointment: BookedAppointment | null
}

export type BookingFlowEvent =
  | { type: "CONTINUE" }
  | { type: "DISQUALIFIED"; exclusion: ScreeningExclusion }
  // The booking call has already succeeded by the time this fires (the preferences screen owns
  // it), so the appointment rides along for the confirmation screen.
  | { type: "CONFIRM_BOOKING"; preferences: BookingPreferences; slot: SelectedSlot; appointment: BookedAppointment }

export const bookingFlowMachine = setup({
  types: {
    input: {} as BookingFlowInput,
    context: {} as BookingFlowContext,
    events: {} as BookingFlowEvent
  },
  guards: {
    // Skip the consent page when the member has already signed the current required
    // consents — fed in as machine input from getConsentStatus.
    alreadyConsented: ({ context }) => context.alreadySignedConsent,
    // Exit guard, still stubbed off until wired to real data.
    hasNoAvailability: () => false // TODO: derive from the availability lookup
  },
  actions: {
    recordExclusion: assign({
      exclusion: ({ event }) => (event.type === "DISQUALIFIED" ? event.exclusion : null)
    }),
    recordBooking: assign({
      preferences: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.preferences : null),
      selectedSlot: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.slot : null),
      appointment: ({ event }) => (event.type === "CONFIRM_BOOKING" ? event.appointment : null)
    })
  }
}).createMachine({
  id: "bookingFlow",
  // Provider finder launches the flow, so the machine itself starts at Consent.
  initial: "consent",
  context: ({ input }) => ({
    alreadySignedConsent: input.alreadySignedConsent,
    program: input.program,
    exclusion: null,
    preferences: null,
    selectedSlot: null,
    appointment: null
  }),
  states: {
    consent: {
      always: [{ target: "healthQuestions", guard: "alreadyConsented" }],
      on: { CONTINUE: "healthQuestions" }
    },

    // Nested so it can grow into multiple (possibly member-data-driven) views without touching
    // the outer states. The page sends DISQUALIFIED on an ineligible result, else CONTINUE.
    healthQuestions: {
      initial: "view",
      states: {
        view: {
          on: {
            CONTINUE: { target: "done" },
            DISQUALIFIED: { target: "#notEligible", actions: "recordExclusion" }
          }
        },
        done: { type: "final" }
      },
      onDone: { target: "seeAvailability" }
    },

    // Nested so the availability journey can grow without touching the outer states. The
    // preferences view now also hosts the date + time picker (MEM-716): the member sets
    // visit type + location, picks a slot from availability, and confirms — recording the
    // preferences and chosen slot. Empty availability is handled inline on that screen, so
    // the onDone branch's `hasNoAvailability` guard stays stubbed until a flow needs a
    // terminal empty state.
    seeAvailability: {
      initial: "preferences",
      states: {
        preferences: {
          on: {
            CONFIRM_BOOKING: { target: "done", actions: "recordBooking" }
          }
        },
        done: { type: "final" }
      },
      onDone: [{ target: "noAvailability", guard: "hasNoAvailability" }, { target: "booked" }]
    },

    booked: { type: "final" },
    notEligible: { id: "notEligible", type: "final" },
    noAvailability: { type: "final" }
  }
})
